import { sub, type Duration } from "date-fns";

import { Season } from "@mysnowscape/snow-lib";

import type {
  RecencyFilter,
  RouteGenerationInputs,
  RouteType,
} from "../../../../../types/routeBuilder";

export const RECENCY_OPTIONS: Record<
  RouteType,
  Array<{ value: RecencyFilter; label: string }>
> = {
  temporary: [
    { value: null, label: "None" },
    { value: "2h", label: "Last 2 hours" },
    { value: "6h", label: "Last 6 hours" },
    { value: "24h", label: "Last 24 hours" },
  ],
  recurring: [
    { value: null, label: "None" },
    { value: "1m", label: "Last Month" },
    { value: "6m", label: "Last 6 Months" },
    { value: "1y", label: "Last Year" },
  ],
};

export function shouldShowRecency(
  routeType: RouteType,
  season: Season,
): boolean {
  return (
    routeType === "recurring" ||
    (routeType === "temporary" && season === Season.snow)
  );
}

const RECENCY_DURATIONS: Record<Exclude<RecencyFilter, null>, Duration> = {
  "2h": { hours: 2 },
  "6h": { hours: 6 },
  "24h": { hours: 24 },
  "1m": { months: 1 },
  "6m": { months: 6 },
  "1y": { years: 1 },
};

/** ISO timestamp for `filter[servicedAfter]`, or undefined when unset. */
export function recencyToServicedAfter(
  recency: RecencyFilter | undefined,
  now: Date = new Date(),
): string | undefined {
  if (!recency) return undefined;
  return sub(now, RECENCY_DURATIONS[recency]).toISOString();
}

/**
 * Keys of every service ticked on a property for one season, in their saved
 * order. Some saved options have no key, so those are skipped.
 */
export function checkedServiceKeys(
  seasonDetails:
    | { serviceOptions?: Array<{ key?: string; checked?: boolean }> }
    | null
    | undefined,
): string[] {
  return (seasonDetails?.serviceOptions ?? []).flatMap((option) =>
    option.checked && option.key ? [option.key] : [],
  );
}

/** Fallback display when a company service option has no label. */
export function serviceTypeLabel(service: string): string {
  const spaced = service
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) return service;
  return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
}

export function crewBaseLabel(season: Season, service?: string | null): string {
  const seasonLabel = season === Season.landscape ? "Landscape" : "Snow";
  if (!service) return `${seasonLabel} Crew`;
  return `${seasonLabel} ${serviceTypeLabel(service)} Crew`;
}

/** Suggested prefix for the wizard route-name field. */
export function suggestRouteNamePrefix(inputs: RouteGenerationInputs): string {
  const selected = inputs.propertyFilters.serviceTypes ?? [];
  const primary = selected[0] ?? null;
  return crewBaseLabel(inputs.season, primary);
}

/** Expand a prefix into numbered route names: `Snow Crew 1`, `Snow Crew 2`, … */
export function expandRouteNamePrefix(
  prefix: string,
  routeCount: number,
): string[] {
  const base = prefix.trim() || "Crew";
  return Array.from({ length: Math.max(routeCount, 1) }, (_, index) => {
    return `${base} ${index + 1}`;
  });
}
